import { TrendingUp, TrendingDown, Minus, Image } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-darker border border-slate-dark/40 rounded px-3 py-2 text-xs text-slate-lightest max-w-[200px]">
        <p className="font-bold mb-1">{payload[0].payload.title || `Pin ${payload[0].payload.name}`}</p>
        <p>Guardados: <span className="font-bold text-slate-lighter">{payload[0].value.toLocaleString()}</span></p>
      </div>
    )
  }
  return null
}

const DIRECTION_ICONS = {
  rising:  TrendingUp,
  falling: TrendingDown,
  stable:  Minus,
}

export default function PinterestTrendsPanel({ pinterestData }) {
  if (!pinterestData) return null

  if (pinterestData.not_configured) {
    return (
      <div className="gradient-glow p-5 rounded-xl">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-5 h-5 bg-slate-light rounded-full flex items-center justify-center shrink-0">
            <span className="text-xs font-bold text-dark-bg">P</span>
          </div>
          <h3 className="text-lg font-bold text-white">Pinterest — Interés Visual</h3>
          <span className="text-xs px-2.5 py-0.5 bg-slate-darker border border-slate-dark/40 rounded-full text-slate-medium ml-auto">
            Sin resultados
          </span>
        </div>
        <p className="text-sm text-slate-light">
          No se encontraron pins para este keyword. Intenta con un término más general.
        </p>
      </div>
    )
  }

  const {
    keyword, interest_score = 0, total_pins = 0, pins_with_saves = 0,
    avg_saves = 0, max_saves = 0, savesChart = [], related = [],
    trend_direction = 'stable', source,
  } = pinterestData

  const DirIcon = DIRECTION_ICONS[trend_direction] || Minus
  const dirColor = trend_direction === 'rising'
    ? 'text-green-400' : trend_direction === 'falling'
    ? 'text-red-400' : 'text-slate-light'

  const barColors = savesChart.map((_, i) =>
    i === 0 ? '#e2e2e2' : i === 1 ? '#b8b8b8' : i === 2 ? '#909090' : '#606060'
  )

  return (
    <div className="gradient-glow p-6 rounded-xl card-hover">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-5 h-5 bg-slate-light rounded-full flex items-center justify-center shrink-0">
          <span className="text-xs font-bold text-dark-bg">P</span>
        </div>
        <h3 className="text-lg font-bold text-white">Pinterest — Interés Visual</h3>
        <span className="text-xs px-2 py-0.5 bg-slate-darker border border-slate-dark/30 text-slate-medium rounded ml-auto">
          RapidAPI Scraper
        </span>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="p-3 bg-slate-darker border border-slate-dark/30 rounded-lg text-center">
          <p className="text-2xl font-bold text-slate-lighter">{interest_score}</p>
          <p className="text-xs text-slate-light">Interés visual</p>
          <p className="text-xs text-slate-medium">/100</p>
        </div>
        <div className="p-3 bg-slate-darker border border-slate-dark/30 rounded-lg text-center">
          <p className="text-2xl font-bold text-slate-lighter">{total_pins}</p>
          <p className="text-xs text-slate-light">Pins encontrados</p>
          <p className="text-xs text-slate-medium">en búsqueda</p>
        </div>
        <div className="p-3 bg-slate-darker border border-slate-dark/30 rounded-lg text-center">
          <p className="text-2xl font-bold text-slate-lighter">{avg_saves.toLocaleString()}</p>
          <p className="text-xs text-slate-light">Guardados</p>
          <p className="text-xs text-slate-medium">promedio/pin</p>
        </div>
        <div className="p-3 bg-slate-darker border border-slate-dark/30 rounded-lg text-center">
          <DirIcon className={`w-5 h-5 mx-auto mb-1 ${dirColor}`} />
          <p className={`text-sm font-bold ${dirColor}`}>
            {trend_direction === 'rising' ? 'Alto interés' : trend_direction === 'falling' ? 'Bajo interés' : 'Interés moderado'}
          </p>
          <p className="text-xs text-slate-light">Nivel</p>
        </div>
      </div>

      {/* Gráfico de saves por pin */}
      {savesChart.length > 0 && (
        <div className="mb-5">
          <p className="text-xs text-slate-light font-medium mb-2 uppercase tracking-wide">
            Pins más guardados (top {savesChart.length})
          </p>
          {savesChart.every(d => d.saves === 0) ? (
            <div className="p-4 bg-slate-darker border border-slate-dark/20 rounded-lg text-center">
              <p className="text-sm text-slate-medium">
                Los pins encontrados aún no tienen guardados registrados. El producto existe en Pinterest pero sin engagement medible.
              </p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={savesChart} margin={{ top: 4, right: 4, bottom: 4, left: 0 }}>
                <XAxis dataKey="name" tick={{ fill: '#7a7a7a', fontSize: 10 }} />
                <YAxis tick={{ fill: '#7a7a7a', fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="saves" radius={[3, 3, 0, 0]}>
                  {savesChart.map((_, i) => (
                    <Cell key={i} fill={barColors[i] || '#505050'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {/* Términos relacionados */}
      {related.length > 0 && (
        <div className="mb-5">
          <p className="text-xs text-slate-light font-medium mb-2 uppercase tracking-wide">Términos relacionados en Pinterest</p>
          <div className="flex flex-wrap gap-2">
            {related.map((term, i) => (
              <span key={i} className="text-xs px-2.5 py-1 bg-slate-darker border border-slate-dark/30 text-slate-lightest rounded-full">
                {term}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Nota sobre API */}
      <div className="p-3 bg-slate-darker border border-slate-dark/20 rounded-lg">
        <p className="text-xs text-slate-medium leading-relaxed">
          <span className="text-slate-light font-medium">Datos actuales:</span> pins y guardados en tiempo real vía RapidAPI.
          Para ver la <span className="text-slate-light">tendencia histórica</span> de Pinterest (gráfico de interés mes a mes),
          activa <span className="text-slate-lighter font-medium">Pinterest Business API</span> en Configuración → APIs → Pinterest.
        </p>
      </div>
    </div>
  )
}
